import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { gql } from 'apollo-angular';
import { OuiAutocomplete, OuiAutocompleteOption } from 'omnium-ui/form-field';
import { QueryManagerService } from 'finzzle-angular-utils';
import { debounceTime, distinctUntilChanged, lastValueFrom, Subject, takeUntil } from 'rxjs';
import { Commande, CommandesFinancieresPaginatedCollectionSegment } from '../../models/generated/graphql';
const ALLCONTRATS = gql`
  query allCommandesFinancieresPaginated($skip: Int, $take: Int, $where: CommandeFilterInput) {
    allCommandesFinancieresPaginated: commandesPaginated(skip: $skip, take: $take, where: $where) {
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      items {
        id
        nom
        produitId
        numeroContrat
        dateActe
        produit {
          id
          nom
          nomTechniqueProduitBox
        }
        investisseurCommande {
          investisseurId
          investisseur {
            investisseurEntite {
              displayName
            }
          }
        }
      }
      totalCount
    }
  }
`;
@Component({
  selector: 'lib-search-commande-autocomplete',
  templateUrl: './search-commande-autocomplete.component.html',
  styleUrls: ['./search-commande-autocomplete.component.scss'],
  standalone: false,
})
export class SearchCommandeAutocompleteComponent {
  @ViewChild('autocomplete') autocomplete: OuiAutocomplete<any>;
  @Input() label: string = 'Numéro de contrat';
  @Input() enveloppeId: number | null | undefined = null;
  @Output() onCommandeIdSelected = new EventEmitter<number | null | undefined>();
  @Output() onCommandeSelected = new EventEmitter<Commande | undefined>();

  control: FormControl;
  autocompleteOptions: OuiAutocompleteOption[] = [];
  isLoading: boolean = false;
  commandes: Commande[] = [];
  private ngUnsubscribe = new Subject<void>();

  constructor(private queryManager: QueryManagerService) {}
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnInit(): void {
    this.control = new FormControl<number | undefined>(undefined);
    this.listenToControlChange();
  }
  listenToControlChange(): void {
    this.control.valueChanges
      .pipe(debounceTime(350), distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (Number.isInteger(value)) {
          const commandeFound = this.commandes.find(commande => commande.id == value);
          if (commandeFound) {
            this.onCommandeIdSelected.emit(commandeFound.id);
            this.onCommandeSelected.emit(commandeFound);
          }
          return;
        }
        this.onCommandeIdSelected.emit(null);
        this.onCommandeSelected.emit(undefined);
        if (value) {
          this.fecthData(value.toString());
          return;
        }
        this.fecthData();
      });
  }

  async fecthData(contratNumber: string = '') {
    this.isLoading = true;
    const variables = {
      skip: 0,
      take: 25,
      where: {
        numeroContrat: { contains: contratNumber },
        produit: { enveloppeNavigation: { code: { eq: this.enveloppeId } }, etat: { in: [2, 3] } }, // produit ouvert et clos à la souscription
      },
    };
    const result = await lastValueFrom(
      this.queryManager.query<{
        allCommandesFinancieresPaginated: CommandesFinancieresPaginatedCollectionSegment;
      }>({
        query: ALLCONTRATS,
        variables,
      })
    );
    this.commandes = result.data.allCommandesFinancieresPaginated.items ?? [];
    this.setOptions();
    this.isLoading = false;
  }

  setOptions() {
    this.autocompleteOptions = this.commandes.map(commande => {
      const produit = commande.produit?.nomTechniqueProduitBox ?? commande.produit?.nom ?? 'produit non défini';
      const numero = commande.numeroContrat;
      const investisseur =
        commande.investisseurCommande[0]?.investisseur?.investisseurEntite?.displayName ?? 'investisseur inconnu';
      return {
        label: `${numero} - ${produit} - ${investisseur}`,
        value: commande.id,
      };
    });
  }

  // use to set the autocomplete value outside this component
  setControlValue(valueId: number | null | undefined) {
    if (valueId) {
      this.control.setValue(valueId);
    } else {
      this.control.setValue(null);
      this.control.reset();
      this.autocomplete.empty();
    }
    this.control.updateValueAndValidity();
  }
}
